import { inject, Injectable } from '@angular/core';
import { SignInCodeResponseApiObject, SignInResponseApiObject } from 'app/core/api/auth';
import { AuthApi } from 'app/core/api/auth.api';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { TokenStorage } from 'app/core/auth/token-storage';
import { UserService } from 'app/core/user/user.service';
import { catchError, defer, map, Observable, of, tap, throwError } from 'rxjs';

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _authApi = inject(AuthApi);
    private _userService = inject(UserService);
    private _tokenStorage = inject(TokenStorage);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        this._tokenStorage.token = token;
    }

    get accessToken(): string
    {
        return this._tokenStorage.token;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._authApi.forgotPassword({email});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, email: string, passwordResetToken: string): Observable<any>
    {
        return this._authApi.resetPassword({
            password,
            email,
            passwordResetToken
        });
    }

    /**
     * Change the signed-in user's password
     *
     * @param currentPassword
     * @param newPassword
     */
    changePassword(currentPassword: string, newPassword: string): Observable<any>
    {
        return this._authApi.changePassword({
            currentPassword,
            newPassword,
        });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<SignInResponseApiObject>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._authApi.signIn(credentials);
    }

    /**
     * Answer the second-factor challenge and get a session.
     *
     * `methodId` names which factor is being answered. There is only one today, so the server
     * would resolve it either way — sending it keeps the client honest for when there is a choice.
     */
    signInTwoFa(credentials: { email: string; code: string; challengeToken: string; methodId?: string }): Observable<SignInCodeResponseApiObject>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._authApi.signInCode(credentials).pipe(
            tap((response) =>
            {
                // Store the access token in the local storage
                this.accessToken = response.token;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;
            }),
        );
    }


    /**
     * Complete a first-time enrolment, which also signs the user in.
     *
     * It used to mint nothing and the caller replayed the same code against signInTwoFa. That
     * only worked because a TOTP code stays valid for its window — an emailed code is single-use
     * and already spent by then.
     *
     * Errors are left on the error channel. This used to catchError into the success channel,
     * which meant every caller had to remember to check `res.error` and a genuine failure read
     * as a success if they forgot.
     */
    verifyMfa(mfaData: { email: string; methodId: string; code: string; challengeToken: string }): Observable<SignInCodeResponseApiObject>
    {
        return this._authApi.verifyMfa(mfaData).pipe(
            tap((response) =>
            {
                this.accessToken = response.token;
                this._authenticated = true;
                this._userService.user = response.user;
            }),
        );
    }

    /**
     * Sign in using the access token
     *
     * The token carries the user's claims, so this resolves the user by decoding it
     * locally rather than by making a request. Returns false if the token cannot be
     * decoded, which leaves the caller unauthenticated.
     */
    signInUsingToken(): Observable<boolean>
    {
        // Deferred so that a decode failure surfaces as an observable error
        // instead of being thrown synchronously past catchError.
        return defer(() => this._userService.get()).pipe(
            tap((user) =>
            {
                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = user;
            }),
            map(() => true),
            catchError(() => of(false)),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        this._tokenStorage.clear();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._authApi.signUp(user);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
